import { useCallback, useLayoutEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from './supabase';
import { getUsableSessionAccessToken } from './sessionAccessToken';
import { AccountRequestError } from './accountRequest';
export { AccountRequestError, accountRequestMessage } from './accountRequest';

export const ACCOUNT_REQUEST_TIMEOUT_MS = 30_000;

type RequestAuth = {
  ownerId: string;
  accessToken: string;
  signal: AbortSignal;
  assertCurrent: () => void;
};

// One deadline covers session recovery, the response body, and the final action.
// Aborting locally does not imply that the server rolled back its request.
export const useAuthenticatedRequest = () => {
  const { user, session } = useAuth();
  const authRef = useRef({ user, session });
  const generationRef = useRef(0);
  const controllersRef = useRef(new Set<AbortController>());

  useLayoutEffect(() => {
    authRef.current = { user, session };
  }, [user, session]);

  useLayoutEffect(() => {
    generationRef.current += 1;
    const controllers = controllersRef.current;
    return () => {
      generationRef.current += 1;
      controllers.forEach(controller => controller.abort());
      controllers.clear();
    };
  }, [user?.id]);

  return useCallback(async <T,>(operation: (auth: RequestAuth) => Promise<T>, timeoutMs = ACCOUNT_REQUEST_TIMEOUT_MS): Promise<T> => {
    const ownerId = authRef.current.user?.id;
    if (!ownerId) {
      throw new AccountRequestError('authentication', 'Please sign in again to continue.');
    }
    const generation = generationRef.current;
    const controller = new AbortController();
    controllersRef.current.add(controller);
    let timedOut = false;
    const assertCurrent = () => {
      if (controller.signal.aborted || generation !== generationRef.current || authRef.current.user?.id !== ownerId) {
        throw new AccountRequestError(
          timedOut ? 'timeout' : 'canceled',
          timedOut ? 'This is taking longer than expected. Please try again, or contact hello@topclip.ai.' : 'Your account changed. Please try again.'
        );
      }
    };
    let onAbort: () => void = () => undefined;
    const interrupted = new Promise<never>((_, reject) => {
      onAbort = () => {
        try { assertCurrent(); } catch (error) { reject(error); }
      };
      controller.signal.addEventListener('abort', onAbort, { once: true });
    });
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    // A bounded workflow may shorten its final request, never extend the limit.
    }, Math.max(1, Math.min(timeoutMs, ACCOUNT_REQUEST_TIMEOUT_MS)));

    try {
      return await Promise.race([
        interrupted,
        (async () => {
          const currentSession = authRef.current.session;
          let accessToken = currentSession?.user?.id && currentSession.user.id !== ownerId
            ? null
            : getUsableSessionAccessToken(currentSession);
          if (!accessToken) {
            // Supabase refreshes an expired stored session when possible.
            const { data, error } = await supabase.auth.getSession();
            assertCurrent();
            if (error || data.session?.user.id !== ownerId) {
              throw new AccountRequestError('authentication', 'Please sign in again to continue.');
            }
            accessToken = getUsableSessionAccessToken(data.session);
          }
          if (!accessToken) {
            throw new AccountRequestError('authentication', 'Please sign in again to continue.');
          }
          assertCurrent();
          const result = await operation({ ownerId, accessToken, signal: controller.signal, assertCurrent });
          assertCurrent();
          return result;
        })(),
      ]);
    } finally {
      window.clearTimeout(timer);
      controller.signal.removeEventListener('abort', onAbort);
      controllersRef.current.delete(controller);
    }
  }, []);
};
